import { useState } from "react";
import {
    ArrowLeftIcon,
    Bars3Icon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { QuinnChatSummary, QuinnCopy } from "../../types";
import { groupChats } from "../../utils/chatHistory.ts";
import { ChatRow } from "./ChatRow.tsx";

type QuinnHistorySidebarProps = {
    chats: QuinnChatSummary[];
    activeChatId: string | null;
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
    onOpenChat: (chatId: string) => void;
    onNewChat: () => void;
    onBack: () => void;
};

export function QuinnHistorySidebar({
    chats,
    activeChatId,
    isCollapsed,
    onToggleCollapsed,
    onOpenChat,
    onNewChat,
    onBack,
}: QuinnHistorySidebarProps) {
    const [search, setSearch] = useState("");
    const groups = groupChats(chats, search);
    const toggleLabel = isCollapsed ? QuinnCopy.ShowChatHistory : QuinnCopy.HideChatHistory;

    if (isCollapsed) {
        return (
            <aside className="flex w-14 shrink-0 flex-col items-center gap-2 border-r border-gray-200 bg-gray-50 py-3">
                <button
                    type="button"
                    onClick={onToggleCollapsed}
                    className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                >
                    <Bars3Icon className="h-5 w-5" />
                </button>
                <button
                    type="button"
                    onClick={onNewChat}
                    className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    aria-label={QuinnCopy.NewChat}
                    title={QuinnCopy.NewChat}
                >
                    <PencilSquareIcon className="h-5 w-5" />
                </button>
                <button
                    type="button"
                    onClick={onBack}
                    className="mt-auto rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    aria-label={QuinnCopy.BackToOverview}
                    title={QuinnCopy.BackToOverview}
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
            </aside>
        );
    }

    return (
        <aside className="flex w-72 shrink-0 flex-col gap-3 border-r border-gray-200 bg-gray-50 px-3 py-3">
            <div className="flex items-center gap-2 px-1">
                <img src="/quinn-green-bg.png" alt="" className="h-7 w-7 shrink-0 rounded-lg" />
                <span className="flex-1 text-sm font-semibold text-gray-900">{QuinnCopy.Quinn}</span>
                <button
                    type="button"
                    onClick={onToggleCollapsed}
                    className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                >
                    <Bars3Icon className="h-4 w-4" />
                </button>
            </div>

            <button
                type="button"
                onClick={onNewChat}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 transition-colors hover:bg-gray-100"
            >
                <PencilSquareIcon className="h-4 w-4 text-gray-500" />
                {QuinnCopy.NewChat}
            </button>

            <label className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-gray-200">
                <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-gray-400" />
                <span className="sr-only">{QuinnCopy.SearchChats}</span>
                <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={QuinnCopy.SearchChats}
                    className="w-full border-0 bg-transparent p-0 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                />
            </label>

            <nav aria-label={QuinnCopy.ChatHistory} className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto">
                {groups.map((group) => (
                    <div key={group.label} className="pb-2">
                        <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-400">
                            {group.label}
                        </p>
                        {group.items.map((chat) => (
                            <ChatRow
                                key={chat.id}
                                chat={chat}
                                size="comfortable"
                                isActive={chat.id === activeChatId}
                                onSelect={() => onOpenChat(chat.id)}
                            />
                        ))}
                    </div>
                ))}
                {groups.length === 0 && <p className="px-2.5 py-3 text-sm text-gray-400">{QuinnCopy.NoChatsFound}</p>}
            </nav>

            <button
                type="button"
                onClick={onBack}
                className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
                <ArrowLeftIcon className="h-4 w-4 text-gray-500" />
                {QuinnCopy.BackToOverview}
            </button>
        </aside>
    );
}
